import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import {
  clubBillingSchema,
  updateClubSchema,
  type ClubDto,
  type UpdateClubInput,
} from "@tc/shared";
import { Button, Card, ClubAvatar, Field, Icon, Select, Tag, TextInput } from "@/components/ui";
import { useClubs } from "@/lib/clubs/clubs";
import { adminApi, ApiError } from "./api";

interface FormState {
  name: string;
  shortCode: string;
  primaryContact: string;
  contactPhone: string;
  contactEmail: string;
  defaultLanguage: ClubDto["defaultLanguage"];
  status: ClubDto["status"];
  companyName: string;
  taxId: string;
  street: string;
  postalCode: string;
  city: string;
  province: string;
  accountingEmail: string;
}

type Errors = Partial<Record<keyof FormState, string>>;

/**
 * Back-office club edit (TW-3527 billing + short code). One form, one Save: the club profile
 * (name, short code, language, status) is PATCHed and the invoice details are PUT to the billing
 * endpoint (which versions the legal identity). Both shared Zod schemas drive validation, so the
 * form and the API agree on what's valid. Note: the redesign omits language/status, but they're
 * kept here so a club can still be suspended or have its default language changed.
 */
export function ClubEditPage() {
  const { t } = useTranslation();
  const { clubId = "" } = useParams();
  const navigate = useNavigate();
  const { clubs, refresh } = useClubs();
  const club = clubs.find((c) => c.id === clubId);

  const [form, setForm] = useState<FormState | null>(null);
  const [errors, setErrors] = useState<Errors>({});
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Seed the form from the club (profile) and its billing version (invoice details) once loaded.
  useEffect(() => {
    let alive = true;
    if (!club) return;
    adminApi
      .getClubBilling(clubId)
      .then((b) => {
        if (!alive) return;
        setForm({
          name: club.name,
          shortCode: club.shortCode,
          primaryContact: club.primaryContact,
          contactPhone: club.contactPhone,
          contactEmail: club.contactEmail,
          defaultLanguage: club.defaultLanguage,
          status: club.status,
          companyName: b.companyName,
          taxId: b.taxId,
          street: b.street,
          postalCode: b.postalCode,
          city: b.city,
          province: b.province,
          accountingEmail: b.accountingEmail,
        });
      })
      .catch(() => alive && setError(t("clubs.saveError")));
    return () => {
      alive = false;
    };
  }, [club, clubId, t]);

  const languageOptions = useMemo(
    () => [
      { value: "es", label: "Español" },
      { value: "en", label: "English" },
    ],
    [],
  );

  function set<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((f) => (f ? { ...f, [key]: value } : f));
    if (errors[key]) setErrors((e) => ({ ...e, [key]: undefined }));
  }

  async function onSave() {
    if (!form) return;
    setError(null);
    const next: Errors = {};

    const profile = updateClubSchema.safeParse({
      name: form.name,
      shortCode: form.shortCode,
      primaryContact: form.primaryContact,
      contactPhone: form.contactPhone,
      contactEmail: form.contactEmail,
      defaultLanguage: form.defaultLanguage,
      status: form.status,
    });
    if (!profile.success) {
      for (const issue of profile.error.issues) {
        const f = issue.path[0] as keyof FormState;
        if (f && !next[f]) next[f] = issue.message;
      }
    }

    const billing = clubBillingSchema.safeParse({
      companyName: form.companyName,
      taxId: form.taxId,
      street: form.street,
      postalCode: form.postalCode,
      city: form.city,
      province: form.province,
      accountingEmail: form.accountingEmail,
    });
    if (!billing.success) {
      for (const issue of billing.error.issues) {
        const f = issue.path[0] as keyof FormState;
        if (f && !next[f]) next[f] = issue.message;
      }
    }

    if (!profile.success || !billing.success) {
      setErrors(next);
      return;
    }

    setSaving(true);
    try {
      await adminApi.updateClub(clubId, profile.data as UpdateClubInput);
      await adminApi.updateClubBilling(clubId, billing.data);
      await refresh();
      navigate(`/clubs/${clubId}`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t("clubs.saveError"));
      setSaving(false);
    }
  }

  if (!club || !form) {
    return (
      <div className="page">
        <div className="empty">{t("common.comingSoon")}</div>
      </div>
    );
  }

  return (
    <div className="page">
      <button type="button" className="link admin-back" onClick={() => navigate(`/clubs/${clubId}`)}>
        <Icon name="chevD" size={14} style={{ transform: "rotate(90deg)" }} /> {t("clubs.backToClubs")}
      </button>

      <div className="page-head row-between club-detail-head">
        <div className="club-detail-id">
          <ClubAvatar name={club.name} seed={club.id} lg />
          <div>
            <div className="club-detail-title">
              <h1>{club.name}</h1>
              <Tag variant="neutral">{form.shortCode || club.shortCode}</Tag>
            </div>
            <div className="sub">{t("clubs.tripsSummary", { trips: club.tripCount, open: club.openTripCount })}</div>
          </div>
        </div>
        <div className="admin-row-actions">
          <Button variant="ghost" onClick={() => navigate(`/clubs/${clubId}`)} disabled={saving}>
            {t("actions.cancel")}
          </Button>
          <Button variant="primary" icon="check" onClick={onSave} disabled={saving}>
            {t("actions.save")}
          </Button>
        </div>
      </div>

      {error && (
        <div className="auth-alert" style={{ marginBottom: 18 }} role="alert">
          <Icon name="alert" size={16} className="aa-ic" />
          <div className="aa-body">{error}</div>
        </div>
      )}

      <Card>
        <div className="set-section-head">
          <h2>{t("clubs.profileTitle")}</h2>
          <div className="sub">{t("clubs.profileSub")}</div>
        </div>
        <div className="form-grid" style={{ marginTop: 18 }}>
          <Field label={t("clubs.name")} required error={errors.name} full>
            <TextInput value={form.name} onChange={(v) => set("name", v)} />
          </Field>
          <Field label={t("clubs.shortCode")} required error={errors.shortCode} hint={t("clubs.shortCodeHint")}>
            <TextInput value={form.shortCode} onChange={(v) => set("shortCode", v)} />
          </Field>
          <Field label={t("clubs.defaultLanguage")} error={errors.defaultLanguage}>
            <Select
              value={form.defaultLanguage}
              onChange={(v) => set("defaultLanguage", v as ClubDto["defaultLanguage"])}
              options={languageOptions}
            />
          </Field>
          <Field label={t("clubs.status")} error={errors.status}>
            <Select
              value={form.status}
              onChange={(v) => set("status", v as ClubDto["status"])}
              options={[
                { value: "active", label: t("clubs.statusActive") },
                { value: "suspended", label: t("clubs.statusSuspended") },
              ]}
            />
          </Field>
          <Field label={t("clubs.primaryContact")} error={errors.primaryContact} full>
            <TextInput icon="user" value={form.primaryContact} onChange={(v) => set("primaryContact", v)} />
          </Field>
          <Field label={t("clubs.contactPhone")} error={errors.contactPhone}>
            <TextInput type="tel" value={form.contactPhone} onChange={(v) => set("contactPhone", v)} />
          </Field>
          <Field label={t("clubs.contactEmail")} error={errors.contactEmail}>
            <TextInput icon="mail" type="email" value={form.contactEmail} onChange={(v) => set("contactEmail", v)} />
          </Field>
        </div>
      </Card>

      <Card style={{ marginTop: 18 }}>
        <div className="set-section-head">
          <h2>{t("clubs.invoiceTitle")}</h2>
          <div className="sub">{t("clubs.invoiceSub")}</div>
        </div>
        <div className="form-grid" style={{ marginTop: 18 }}>
          <Field label={t("settings.billing.companyName")} required error={errors.companyName} full>
            <TextInput icon="building" value={form.companyName} onChange={(v) => set("companyName", v)} />
          </Field>
          <Field
            label={t("settings.billing.taxId")}
            required
            error={errors.taxId}
            hint={t("settings.billing.taxIdHint")}
            full
          >
            <TextInput value={form.taxId} onChange={(v) => set("taxId", v)} />
          </Field>
          <Field label={t("settings.billing.street")} error={errors.street} full>
            <TextInput value={form.street} onChange={(v) => set("street", v)} />
          </Field>
          <Field label={t("settings.billing.postalCode")} error={errors.postalCode}>
            <TextInput value={form.postalCode} onChange={(v) => set("postalCode", v)} />
          </Field>
          <Field label={t("settings.billing.city")} error={errors.city}>
            <TextInput value={form.city} onChange={(v) => set("city", v)} />
          </Field>
          <Field label={t("settings.billing.province")} error={errors.province} full>
            <TextInput value={form.province} onChange={(v) => set("province", v)} />
          </Field>
          <Field
            label={t("settings.billing.accountingEmail")}
            required
            error={errors.accountingEmail}
            hint={t("settings.billing.accountingEmailHint")}
            full
          >
            <TextInput
              icon="mail"
              type="email"
              value={form.accountingEmail}
              onChange={(v) => set("accountingEmail", v)}
            />
          </Field>
        </div>
      </Card>
    </div>
  );
}
